import { PostStarPatch, RestFeeds, RestUserFeed } from "../../schema-copy/api-schema";
import { JsonStore } from "../json-store";
import { PendingMarkers, SerializedFeedMarker } from "./read-marker-engine";

// Keys inside the user's JsonStore (so actually stored as `<userId>-offline-*`). Split in two
// because read state changes far more often than the rest, and is cheap to write on its own.
export const OFFLINE_LIBRARY_KEY = 'offline-library';
export const OFFLINE_MARKERS_KEY = 'offline-markers';
// Unsent changes. Not part of the snapshot: clearOfflineSnapshot() must never drop them.
export const OFFLINE_OUTBOX_KEY = 'offline-outbox';

/**
 * Changes not yet accepted by the server, consolidated (one merged patch per kind, not a request
 * log), so they survive a reload or app kill while offline. Absent members mean nothing pending.
 */
export interface OfflineOutbox {
  markers?: PendingMarkers;
  starred?: PostStarPatch;
  readLater?: PostStarPatch;
}

/** The downloaded library offline reading needs, except the read state (see OFFLINE_MARKERS_KEY). */
export interface OfflineLibrary {
  /** The last GET /api/feeds result, kept current with every poll and local edit (stars,
   *  folders, subscriptions). Its feedMarkers/bitmaps are the server's copy; the stored
   *  SerializedFeedMarker[] carries the newer local read state on top. */
  data: RestFeeds;
  /** Metadata of feeds that are referenced (starred, read later, unread list) but not
   *  subscribed, so those posts still resolve offline. */
  extraFeeds: Record<string, RestUserFeed>;
  /** When this snapshot was written (ms since epoch). */
  savedAt: number;
}

export function saveOfflineLibrary(store: JsonStore, library: OfflineLibrary): void {
  store.setJson(OFFLINE_LIBRARY_KEY, library);
}

export function saveOfflineMarkers(store: JsonStore, markers: SerializedFeedMarker[]): void {
  store.setJson(OFFLINE_MARKERS_KEY, markers);
}

export function loadOfflineLibrary(store: JsonStore): OfflineLibrary | undefined {
  return store.getJson<OfflineLibrary | undefined>(OFFLINE_LIBRARY_KEY, undefined);
}

export function loadOfflineMarkers(store: JsonStore): SerializedFeedMarker[] {
  return store.getJson<SerializedFeedMarker[]>(OFFLINE_MARKERS_KEY, []);
}

export function loadOfflineOutbox(store: JsonStore): OfflineOutbox | undefined {
  return store.getJson<OfflineOutbox | undefined>(OFFLINE_OUTBOX_KEY, undefined);
}

/** Store the outbox, or delete it when nothing is pending. */
export function saveOfflineOutbox(store: JsonStore, outbox: OfflineOutbox): void {
  if (outbox.markers || outbox.starred || outbox.readLater) store.setJson(OFFLINE_OUTBOX_KEY, outbox);
  else store.remove(OFFLINE_OUTBOX_KEY);
}

export function clearOfflineSnapshot(store: JsonStore): void {
  store.remove(OFFLINE_LIBRARY_KEY);
  store.remove(OFFLINE_MARKERS_KEY);
}
